import Breadcrumbs from '../components/Breadcrumbs';
import Link from '../components/Link';
import AdSlot from '../components/AdSlot';
import ArticleSections from '../components/ArticleSections';
import NotFoundPage from './NotFoundPage';
import { CONTENT_CLASSES } from '../constants/classNames';
import { ADSENSE_SLOTS, ROUTES } from '../constants/config';
import { CATEGORY_LABELS } from '../constants/effects';
import {
  findElementBySlug,
  getAlternateName,
  getElementPlace,
  getElements,
  getPageIndexingPolicy,
  summarizeElementReactions,
} from '../data/sitePages';
import { findElementArticle } from '../data/elementArticles';
import { buildElementPath, buildLabPath } from '../utils/routeUtils';
import '../styles/ContentPage.css';

function ReactionListItem({ element, partner, reaction, path }) {
  return (
    <li className={CONTENT_CLASSES.listItem}>
      <span className={CONTENT_CLASSES.listTitle}>
        {path ? <Link to={path}>{reaction.title}</Link> : `${element.name} + ${partner.name}`}
      </span>
      <span className={CONTENT_CLASSES.listDetail}>
        {reaction.productName}
        {reaction.getEquation() && <span className={CONTENT_CLASSES.equation}> {reaction.getEquation()}</span>}
        {' · '}
        {reaction.conditions}
        {' · '}
        <Link to={buildLabPath([element.id, partner.id])}>Try it</Link>
      </span>
    </li>
  );
}

// The reaction list shows this many at first; the rest stay in the page behind "Show all".
const VISIBLE_REACTION_COUNT = 10;

const countThings = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

/**
 * One sentence that adds up everything the element was mixed with, so the numbers always total.
 */
function describeReactionTotals(element, summary) {
  const name = element.name.toLowerCase();
  const opening = `Altogether we mixed ${name} with ${countThings(summary.testedCount, 'other substance', 'other substances')} in our lab`;
  if (summary.nothingHappensCount === summary.testedCount) return `${opening}, and nothing happens with any of them.`;
  const parts = [
    summary.newSubstanceCount > 0 && `${countThings(summary.newSubstanceCount, 'makes', 'make')} a new substance`,
    summary.mixtureCount > 0 && `${countThings(summary.mixtureCount, 'blends', 'blend')} with it into a mixture`,
    summary.nothingHappensCount > 0 && `nothing happens with ${summary.nothingHappensCount}`,
  ].filter(Boolean);
  const list = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
  return `${opening}: ${list}.`;
}

/**
 * One page per element: simple facts, a written article for the best-known ones, and everything
 * it does with the other substances.
 */
export default function ElementPage({ slug }) {
  const element = findElementBySlug(slug);
  if (!element) return <NotFoundPage />;

  const elements = getElements();
  const index = elements.indexOf(element);
  const previous = elements[index - 1];
  const next = elements[index + 1];
  const { period, group } = getElementPlace(element);
  const alternateName = getAlternateName(element);
  const summary = summarizeElementReactions(element);
  const article = findElementArticle(element.symbol);
  const visibleCompounds = summary.compounds.slice(0, VISIBLE_REACTION_COUNT);
  const hiddenCompounds = summary.compounds.slice(VISIBLE_REACTION_COUNT);
  const facts = [
    ['Symbol', element.symbol],
    ['Atomic number', element.atomicNumber],
    ['Atomic mass', element.atomicMass],
    ['Family', CATEGORY_LABELS[element.category]],
    ['Period', period],
    ['Group', group ?? 'f-block (no group)'],
  ];

  return (
    <article className={CONTENT_CLASSES.root}>
      <Breadcrumbs
        trail={[
          { label: 'Lab', path: ROUTES.LAB },
          { label: 'Elements', path: ROUTES.ELEMENTS },
          { label: element.name, path: buildElementPath(element) },
        ]}
      />

      <header className={CONTENT_CLASSES.hero}>
        <h1 className={CONTENT_CLASSES.heading}>
          {element.name} ({element.symbol})
        </h1>
        {alternateName && <p className={CONTENT_CLASSES.meta}>Also spelled {alternateName}.</p>}
        <p className={CONTENT_CLASSES.lead}>{element.description}</p>
        {article && <p className={CONTENT_CLASSES.text}>{article.intro}</p>}
        <div className={CONTENT_CLASSES.buttonRow}>
          <Link to={buildLabPath([element.id])} className={CONTENT_CLASSES.button}>
            Mix {element.name} in the lab 🧪
          </Link>
        </div>
      </header>

      <section className={CONTENT_CLASSES.section}>
        <h2 className={CONTENT_CLASSES.sectionTitle}>{element.name} facts</h2>
        <dl className={CONTENT_CLASSES.factGrid}>
          {facts.map(([label, value]) => (
            <div key={label} className={CONTENT_CLASSES.fact}>
              <dt className={CONTENT_CLASSES.factLabel}>{label}</dt>
              <dd className={CONTENT_CLASSES.factValue}>{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {article && <ArticleSections sections={article.sections} />}

      {summary.famous.length > 0 && (
        <section className={CONTENT_CLASSES.section}>
          <h2 className={CONTENT_CLASSES.sectionTitle}>Famous reactions with {element.name.toLowerCase()}</h2>
          <ul className={CONTENT_CLASSES.list}>
            {summary.famous.map(({ partner, reaction, path }) => (
              <ReactionListItem key={partner.id} element={element} partner={partner} reaction={reaction} path={path} />
            ))}
          </ul>
        </section>
      )}

      <section className={CONTENT_CLASSES.section}>
        <h2 className={CONTENT_CLASSES.sectionTitle}>What does {element.name.toLowerCase()} react with?</h2>
        {summary.isTooRareToTest ? (
          <p className={CONTENT_CLASSES.text}>
            Only a few atoms of {element.name.toLowerCase()} have ever been made at one time, and they break apart
            very quickly. Nobody has ever had enough to mix it with anything, so in our lab every pairing with{' '}
            {element.name.toLowerCase()} shows "never tried".
          </p>
        ) : (
          <>
            {summary.compounds.length > 0 && (
              <>
                <p className={CONTENT_CLASSES.text}>
                  {element.name} makes a new substance with {summary.compounds.length} other{' '}
                  {summary.compounds.length === 1 ? 'element or chemical' : 'elements and chemicals'} in our lab:
                </p>
                <ul className={CONTENT_CLASSES.list}>
                  {visibleCompounds.map(({ partner, reaction }) => (
                    <ReactionListItem key={partner.id} element={element} partner={partner} reaction={reaction} />
                  ))}
                </ul>
                {hiddenCompounds.length > 0 && (
                  <details className={CONTENT_CLASSES.showAll}>
                    <summary className={CONTENT_CLASSES.showAllToggle}>Show all {summary.compounds.length}</summary>
                    <ul className={CONTENT_CLASSES.list}>
                      {hiddenCompounds.map(({ partner, reaction }) => (
                        <ReactionListItem key={partner.id} element={element} partner={partner} reaction={reaction} />
                      ))}
                    </ul>
                  </details>
                )}
              </>
            )}
            <p className={CONTENT_CLASSES.text}>{describeReactionTotals(element, summary)}</p>
          </>
        )}
      </section>

      <nav className={CONTENT_CLASSES.pager} aria-label="Other elements">
        {previous ? <Link to={buildElementPath(previous)}>← {previous.name}</Link> : <span />}
        <Link to={ROUTES.ELEMENTS}>All elements</Link>
        {next ? <Link to={buildElementPath(next)}>{next.name} →</Link> : <span />}
      </nav>

      {getPageIndexingPolicy().isElementPageIndexable(slug) && <AdSlot slotId={ADSENSE_SLOTS.PAGE_BOTTOM} />}
    </article>
  );
}
